"use client";

import TFLink from "@/Web/Components/TFLink/TFLink";
import TFHorizontalSlider from "@/Web/Components/TFHorizontalSlider/TFHorizontalSlider";
import TFBadge from "@/Web/Components/TFBadge/TFBadge";
import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import apiCall from "@/libs/api";
import { useMemo, useState, useEffect } from "react";
import { useParams } from "next/navigation";
import {
  IoTimeSharp,
  IoHeadsetSharp,
  IoCalendarSharp,
  IoMicSharp,
} from "react-icons/io5";
import { useDirection } from "@/utils/useDirection";
import { useTranslations } from "next-intl";

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}ч ${minutes}м`;
  }
  return `${minutes}м`;
};

const formatTimeAgo = (
  dateString: string,
  t: ReturnType<typeof useTranslations>,
): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t("today");
  if (diffDays === 1) return t("yesterday");
  if (diffDays < 7) return t("daysAgo", { days: diffDays });
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return t("weeksAgo", { weeks });
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return t("monthsAgo", { months });
  }
  const years = Math.floor(diffDays / 365);
  return t("yearsAgo", { years });
};

const PodcastCard = ({
  podcast,
  href,
}: {
  podcast: APIDataSpacesAudio;
  href: string;
}) => {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  const t = useTranslations("web.pages.home.sections.podcasts");
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <TFLink
      href={href}
      className="bg-black/5 rounded-lg hover:bg-black/10 transition-colors h-full flex flex-col group"
    >
      {/* Placeholder для аудио */}
      <div className="relative w-full aspect-[4/3] rounded-t-lg bg-gradient-to-br from-black/5 to-black/10 overflow-hidden">
        {podcast.coverUrl && !imageError && (
          <img
            src={podcast.coverUrl}
            alt={podcast.title}
            className={`w-full h-full object-cover rounded-t-lg ${imageLoaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <IoHeadsetSharp className="w-16 h-16 text-black/25 group-hover:text-black/35 transition-colors" />
        </div>
        {podcast.tags.length > 0 && (
          <TFBadge
            title={podcast.tags[0].title}
            style="gray"
            className={`absolute top-2 ${isRTL ? "right-2" : "left-2"}`}
          />
        )}
      </div>

      <div className="p-3 flex flex-col flex-grow">
        <h3 className="text-base font-semibold text-black line-clamp-2 group-hover:text-black/70 transition-colors mb-2">
          {podcast.title}
        </h3>

        <div className="text-sm text-black/60 mb-3">{podcast.space.name}</div>

        <div className="flex items-center gap-2 text-xs text-black/50 mt-auto">
          {podcast.durationSec && (
            <>
              <div className="flex items-center gap-1">
                <IoTimeSharp className="w-3 h-3 text-black/40" />
                <span>{formatDuration(podcast.durationSec)}</span>
              </div>
              <div className="w-0.5 h-0.5 rounded-full bg-black/20"></div>
            </>
          )}
          <div className="flex items-center gap-1">
            <IoCalendarSharp className="w-3 h-3 text-black/40" />
            <span>{formatTimeAgo(podcast.dates.created, t)}</span>
          </div>
        </div>
      </div>
    </TFLink>
  );
};

export default function SectionPodcasts() {
  const t = useTranslations("web.pages.home.sections.podcasts");
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "en";
  const [itemsPerView, setItemsPerView] = useState(4);
  const [audioItems, setAudioItems] = useState<APIDataSpacesAudio[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const updateItemsPerView = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 4);
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  useEffect(() => {
    const loadAudio = async () => {
      try {
        setIsLoading(true);
        const response = await apiCall("hub/spaces/audios", {
          action: "publicList",
          locale: locale as UILocale,
        });
        setAudioItems((response as any).audios);
      } catch (error) {
        console.error("Failed to load audio:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (locale) {
      loadAudio();
    }
  }, [locale]);

  const featuredPodcasts = useMemo(() => {
    return [...audioItems]
      .sort(
        (a, b) =>
          new Date(b.dates.created).getTime() -
          new Date(a.dates.created).getTime(),
      )
      .slice(0, 12);
  }, [audioItems]);

  if (isLoading || featuredPodcasts.length === 0) {
    return null;
  }

  return (
    <TFLayoutHomeSection
      moreButton={{
        title: t("allPodcasts"),
        href: "/podcasts",
      }}
      sideInfo={{
        title: t("title"),
        description: t("description"),
        statistics: [{ title: t("podcasts"), value: audioItems.length }],
        position: "left",
      }}
    >
      <TFHorizontalSlider
        items={featuredPodcasts}
        renderItem={(podcast) => (
          <PodcastCard podcast={podcast} href={`/podcasts/${podcast.ref}`} />
        )}
        itemWidth={280}
        gap={16}
        autoScroll={false}
        showControls={true}
        itemsPerView={itemsPerView}
        showDots={true}
      />
    </TFLayoutHomeSection>
  );
}
